/**
 * 판정 기록 화면 상수·순수 함수 (결정 D1 — @spec:2026-09-29-처방검토-판정기록-조회화면).
 *
 * 등급·출처 문자열(D-4·D-12)·기간 변환(KST, luxon)·필터 URL 동기화·복귀 offset·CSV 파일명·
 * 카드 request 어댑터·버전 요청 대상을 컴포넌트에서 분리해 단위 테스트로 고정한다(§8 DDD).
 * "오늘"은 kstToday(브라우저 TZ 무관), 사용자가 고른 날짜는 formatDate(로컬 달력) — 섞지 않는다.
 */
import { DateTime } from "luxon";
import type { DoseReviewRequest } from "api/doseReviewService";
import type {
  DoseGrade,
  DoseLabelState,
  DoseReviewLogFilter,
  DoseReviewLogListItem,
  DoseReviewLogRuleVersion,
  DoseReviewLogSource,
  DoseRuleScope,
  DoseReviewLogReceivedPayload,
  DoseReviewLogResolvedPayload,
} from "services/api/doseReviewLogs/types";

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

// ── 등급 문자열 (D-4 표 — 요약·목록 배지·등급 필터 공통) ───────────────────────────
export const GRADE_ORDER: DoseGrade[] = [
  "critical",
  "caution",
  "warning",
  "reference",
  "unevaluated",
  "appropriate",
];

export const GRADE_LABELS: Record<DoseGrade, string> = {
  critical: "위험(성분 중복·미승인 룰 포함)",
  caution: "1회 과량",
  warning: "경고(미달 외)",
  reference: "참고(미달·판단 불가)",
  unevaluated: "전부 미판정",
  appropriate: "적정(일부 미판정 포함 가능)",
};

/** null = 원문 삭제됨(purge). 등급 필터 선택지에는 넣지 않는다(내용 필터 대상 아님, D-3). */
export const PURGED_LABEL = "원문 삭제됨";

export const gradeLabel = (g: DoseGrade | null): string =>
  g === null ? PURGED_LABEL : GRADE_LABELS[g] ?? String(g);

/** 등급 필터 선택지(6종) — "원문 삭제됨" 없음(AC L1248). */
export const GRADE_FILTER_OPTIONS = GRADE_ORDER.map((g) => ({ value: g, label: GRADE_LABELS[g] }));

/** 체크박스 "위험·1회 과량·경고만"(alertOnly) — grade IN (critical,caution,warning)(D-4). spec 고정 문자열. */
export const ALERT_ONLY_LABEL = "위험·1회 과량·경고만";
/** 체크박스 "용량 경고 있는 처방만"(doseAlertOnly, D-35). */
export const DOSE_ALERT_ONLY_LABEL = "용량 경고 있는 처방만";
/** 체크박스 "적용만 된 처방도 포함"(ruleScope=applied, D-15). */
export const RULE_SCOPE_APPLIED_LABEL = "적용만 된 처방도 포함";

// ── 출처 문자열 (D-12) ───────────────────────────────────────────────────────────
export const SOURCE_LABELS: Record<string, string> = {
  replay: "재판정 재생",
  prai_admin: "테스트 패널",
  phai_pharmacy: "약국",
  unknown: "미상",
};
/** 목록 밖 값은 원시값 그대로(DB VARCHAR(32)). */
export const sourceLabel = (s: string): string => SOURCE_LABELS[s] ?? s;

export const SOURCE_FILTER_OPTIONS: Array<{ value: "" | DoseReviewLogSource; label: string }> = [
  { value: "", label: "전체" },
  { value: "replay", label: "재판정 재생" },
  { value: "prai_admin", label: "테스트 패널" },
  { value: "phai_pharmacy", label: "약국" },
  { value: "unknown", label: "미상" },
];

/** 정오 표기 상태 필터(F2a — spec 미정, 권고). all은 생략. */
export const LABEL_STATE_OPTIONS: Array<{ value: DoseLabelState; label: string }> = [
  { value: "all", label: "전체" },
  { value: "unlabeled", label: "미표기 줄 있음" },
  { value: "complete", label: "표기 완료" },
];

// ── 문구 상수 ─────────────────────────────────────────────────────────────────────
export const EMPTY_NO_RECORDS_TITLE = "이 기간에 판정 기록이 없습니다";
export const EMPTY_NO_RECORDS_HINT = "판정 API 호출(테스트 패널·재판정 재생·약국 앱)이 기록됩니다";
export const EMPTY_NO_MATCH_TITLE = "조건에 맞는 처방이 없습니다";
export const LIST_ERROR_TEXT = "판정 기록을 불러오지 못했습니다.";
export const INPUT_ERROR_TEXT = "입력값 오류 — 필터 값을 확인해 주세요";
export const DELETED_LOG_TEXT = "삭제된 기록입니다";
export const PURGED_DETAIL_TEXT = "보관기간(180일) 경과로 원문 삭제됨";
export const PERIOD_INVALID_MESSAGE = "기간은 92일 이하로, 시작일이 종료일보다 늦을 수 없습니다";
export const PRODUCT_INVALID_MESSAGE = "2자 이상 50자 이하로 입력하세요";
export const PHARMACY_INPUT_HINT = "호출 사용자 ID(users.id)를 입력하세요";
export const RULE_INPUT_HINT = "룰 ID 전체를 입력하세요(부분 일치 아님)";
export const SOURCE_UNKNOWN_TOOLTIP = "출처 헤더가 없는 호출 — 커버리지 프로브, 헤더 도입 전 호출";
export const PENDING_DOWNGRADE_TOOLTIP =
  "검수 대기 룰이 낸 위험 — 승인 전이라 판정 응답에선 1회 과량으로 나갑니다";
export const CSV_TOO_LARGE_TOAST = "10만 줄을 넘습니다. 필터를 좁혀 주세요";
export const CSV_ABORTED_TOAST = "내보내기가 중단됐습니다";
/** 6124 밖의 CSV 오류 봉투 — 일반 오류 Toast(spec 미정 문구, 권고). */
export const CSV_ERROR_TOAST = "내보내기에 실패했습니다";
export const CSV_EXPORT_LABEL = "CSV 내보내기";
export const DUP_INGREDIENT_BADGE = "성분 중복";
export const PENDING_DOWNGRADE_BADGE = "미승인 룰";
export const RETRY_LABEL = "다시 시도";
export const RESET_FILTER_LABEL = "필터 초기화";
export const PRODUCT_NAME_FALLBACK = "이름 없음";

export const unevaluatedText = (n: number): string => `미판정 ${n}줄`;

/**
 * 호출 사용자(users.id) 축약 — 12자 초과면 앞 8자 + "…"(전체는 title, 결정 H1·§3.10 ⑧).
 */
export const shortUserId = (id: string): string => (id.length > 12 ? `${id.slice(0, 8)}…` : id);

/** 월령 → "N세 M개월"(12개월 미만은 "M개월", null은 "나이 정보 없음"). 상세 처방 요약(상세 화면 개편). */
export const formatPatientAge = (ageMonths: number | null | undefined): string => {
  if (ageMonths == null) return "나이 정보 없음";
  const years = Math.floor(ageMonths / 12);
  const months = ageMonths % 12;
  if (years <= 0) return `${months}개월`;
  return months > 0 ? `${years}세 ${months}개월` : `${years}세`;
};

/** 체중 → "N kg"(없으면 "체중 정보 없음"). */
export const formatWeight = (weightKg: number | null | undefined): string =>
  weightKg != null ? `${weightKg}kg` : "체중 정보 없음";

/** 용법 — 1회량×1일횟수×투여일수(값 없으면 "—"). 처방 목록 표. */
export const formatDosage = (
  dose: number | null | undefined,
  freq: number | null | undefined,
  days: number | null | undefined
): string => `${dose ?? "—"} × ${freq ?? "—"}회 × ${days ?? "—"}일`;

/**
 * 상세 머리·필터의 "호출 사용자" 표시(결정 H1) — pharmacyUserName이 오면 그 이름,
 * 없거나 공백이면 id를 줄여, id도 없으면 "—". 백엔드 pharmacyUserName 추가 전까지는 줄인 id다.
 */
export const pharmacyDisplay = (
  log: Pick<DoseReviewLogListItem, "pharmacyUserId" | "pharmacyUserName">
): string =>
  log.pharmacyUserName?.trim() || (log.pharmacyUserId ? shortUserId(log.pharmacyUserId) : "—");

/** 목록 "정오" 열(F2a — spec 미정, 권고). labelableCount 0이면 "—". */
export const labelProgressCell = (labeledCount: number, labelableCount: number): string =>
  labelableCount === 0 ? "—" : `${labeledCount}/${labelableCount}줄`;
/** 요약 "정오 표기 진행"(F2a). */
export const labelProgressSummary = (labeledItems: number, labelableItems: number): string =>
  `정오 표기 ${labeledItems}/${labelableItems}줄`;

// ── 날짜·시각 (결정 D1 — luxon, 브라우저 TZ 무관) ─────────────────────────────────
/** 오늘(KST) "YYYY-MM-DD". */
export const kstToday = (now: Date = new Date()): string =>
  DateTime.fromJSDate(now).setZone("Asia/Seoul").toISODate() ?? "";

/** 파일명 시각 stamp(KST) "yyyyMMddHHmm". */
export const kstStamp = (now: Date = new Date()): string =>
  DateTime.fromJSDate(now).setZone("Asia/Seoul").toFormat("yyyyMMddHHmm");

/** 날짜 문자열에 n일 더하기(TZ 무관 — 날짜 산술). */
export const addDays = (ymd: string, n: number): string =>
  DateTime.fromISO(ymd, { zone: "UTC" }).plus({ days: n }).toISODate() ?? ymd;

/** 기본 기간 — end = 오늘(KST), start = end − 6일(오늘 포함 7일). */
export const defaultPeriod = (now: Date = new Date()): { start: string; end: string } => {
  const end = kstToday(now);
  return { start: addDays(end, -6), end };
};

/** URL 날짜(종료일 포함) → API 반열림 범위. to = (end+1)T00:00:00+09:00. */
export const periodToApiRange = (start: string, end: string): { from: string; to: string } => ({
  from: `${start}T00:00:00+09:00`,
  to: `${addDays(end, 1)}T00:00:00+09:00`,
});

/** API 범위 → URL 날짜. to가 자정(+09:00)이면 전날이 end, 아니면(방어) to 날짜를 그대로 end(spec 미정, 권고). */
export const apiRangeToPeriod = (range: { from: string; to: string }): {
  start: string;
  end: string;
} => {
  const start = range.from.slice(0, 10);
  const toDate = range.to.slice(0, 10);
  const isMidnight = /T00:00:00\+09:00$/.test(range.to);
  return { start, end: isMidnight ? addDays(toDate, -1) : toDate };
};

/** 기간 사전 검증 — start ≤ end, 포함 일수 ≤ 92. 위반이면 요청을 막고 문구를 보인다(D-12). */
export const validatePeriod = (start: string, end: string): { ok: boolean; message?: string } => {
  const s = DateTime.fromISO(start, { zone: "UTC" });
  const e = DateTime.fromISO(end, { zone: "UTC" });
  if (!s.isValid || !e.isValid) return { ok: false, message: PERIOD_INVALID_MESSAGE };
  if (s.toMillis() > e.toMillis()) return { ok: false, message: PERIOD_INVALID_MESSAGE };
  const inclusiveDays = Math.round(e.diff(s, "days").days) + 1;
  if (inclusiveDays > 92) return { ok: false, message: PERIOD_INVALID_MESSAGE };
  return { ok: true };
};

/** product 사전 검증 — 값이 있을 때만 2~50자. 공백만은 필터 없음과 같다(ok). */
export const validateProduct = (product: string): { ok: boolean; message?: string } => {
  const t = product.trim();
  if (t === "") return { ok: true };
  if (t.length < 2 || t.length > 50) return { ok: false, message: PRODUCT_INVALID_MESSAGE };
  return { ok: true };
};

// ── 필터 상태 ↔ URL ───────────────────────────────────────────────────────────────
/** 화면 필터 상태(URL에서 파싱). start·end는 YYYY-MM-DD, offset은 목록 페이지. */
export interface DoseReviewLogFilterState {
  start: string;
  end: string;
  source?: DoseReviewLogSource;
  pharmacyUserId: string;
  alertOnly: boolean;
  doseAlertOnly: boolean;
  grades: DoseGrade[];
  ruleId: string;
  ruleScope: DoseRuleScope;
  product: string;
  labelState: DoseLabelState;
  offset: number;
}

const parseSource = (v: string | null): DoseReviewLogSource | undefined => {
  if (v === "replay" || v === "prai_admin" || v === "phai_pharmacy" || v === "unknown") return v;
  return undefined;
};

const parseLabelState = (v: string | null): DoseLabelState =>
  v === "unlabeled" || v === "complete" ? v : "all";

const parseOffset = (v: string | null): number => {
  const n = Number(v);
  return Number.isInteger(n) && n > 0 ? n : 0;
};

/** URLSearchParams → 필터 상태. 기간이 없거나(부분 포함) 날짜 형식이 아니면 기본 7일. */
export const parseFilters = (
  sp: URLSearchParams,
  now: Date = new Date()
): DoseReviewLogFilterState => {
  const startRaw = sp.get("start");
  const endRaw = sp.get("end");
  let start = startRaw && DATE_RE.test(startRaw) ? startRaw : "";
  let end = endRaw && DATE_RE.test(endRaw) ? endRaw : "";
  if (!start || !end) {
    const def = defaultPeriod(now);
    start = def.start;
    end = def.end;
  }
  const grades = (sp.get("grades") ?? "")
    .split(",")
    .map((s) => s.trim())
    .filter((g): g is DoseGrade => (GRADE_ORDER as string[]).includes(g));
  return {
    start,
    end,
    source: parseSource(sp.get("source")),
    pharmacyUserId: sp.get("pharmacyUserId") ?? "",
    alertOnly: sp.get("alertOnly") === "true",
    doseAlertOnly: sp.get("doseAlertOnly") === "true",
    grades,
    ruleId: sp.get("ruleId") ?? "",
    ruleScope: sp.get("ruleScope") === "applied" ? "applied" : "alert",
    product: sp.get("product") ?? "",
    labelState: parseLabelState(sp.get("labelState")),
    offset: parseOffset(sp.get("offset")),
  };
};

/** 필터 상태 → URL 쿼리 문자열("?" 없음). 기본값은 생략한다. */
export const filtersToSearch = (f: DoseReviewLogFilterState): string => {
  const sp = new URLSearchParams();
  sp.set("start", f.start);
  sp.set("end", f.end);
  if (f.source) sp.set("source", f.source);
  if (f.pharmacyUserId.trim()) sp.set("pharmacyUserId", f.pharmacyUserId.trim());
  if (f.alertOnly) sp.set("alertOnly", "true");
  if (f.doseAlertOnly) sp.set("doseAlertOnly", "true");
  if (f.grades.length) sp.set("grades", f.grades.join(","));
  if (f.ruleId.trim()) sp.set("ruleId", f.ruleId.trim());
  if (f.ruleScope === "applied") sp.set("ruleScope", "applied");
  if (f.product.trim()) sp.set("product", f.product.trim());
  if (f.labelState !== "all") sp.set("labelState", f.labelState);
  if (f.offset > 0) sp.set("offset", String(f.offset));
  return sp.toString();
};

/** 상세 링크용 쿼리 — offset·limit 제외(L1266). */
export const toDetailSearch = (f: DoseReviewLogFilterState): string =>
  filtersToSearch({ ...f, offset: 0 });

/** 기간 말고 다른 필터가 하나라도 있나(빈 상태 구분 — D-12). ruleScope=applied도 체크박스라 포함. */
export const hasNonPeriodFilter = (f: DoseReviewLogFilterState): boolean =>
  !!f.source ||
  f.pharmacyUserId.trim() !== "" ||
  f.alertOnly ||
  f.doseAlertOnly ||
  f.grades.length > 0 ||
  f.ruleId.trim() !== "" ||
  f.ruleScope === "applied" ||
  f.product.trim() !== "" ||
  f.labelState !== "all";

/** 첫 진입·[필터 초기화] 기본값 — 기간 오늘 포함 7일(KST)·출처/내용 필터 없음·offset 0. */
export const defaultFilterState = (now: Date = new Date()): DoseReviewLogFilterState => {
  const { start, end } = defaultPeriod(now);
  return {
    start,
    end,
    source: undefined,
    pharmacyUserId: "",
    alertOnly: false,
    doseAlertOnly: false,
    grades: [],
    ruleId: "",
    ruleScope: "alert",
    product: "",
    labelState: "all",
    offset: 0,
  };
};

/** 필터 상태 → API 필터(기간은 반열림 변환). 빈 값은 undefined로(toFilterParams가 생략). */
export const toApiFilter = (f: DoseReviewLogFilterState): DoseReviewLogFilter => ({
  ...periodToApiRange(f.start, f.end),
  source: f.source,
  pharmacyUserId: f.pharmacyUserId.trim() || undefined,
  alertOnly: f.alertOnly || undefined,
  doseAlertOnly: f.doseAlertOnly || undefined,
  grades: f.grades.length ? f.grades : undefined,
  ruleId: f.ruleId.trim() || undefined,
  ruleScope: f.ruleScope,
  product: f.product.trim() || undefined,
  labelState: f.labelState,
});

// ── 복귀 offset·CSV 파일명·목록 이름·카드 request·버전 대상 ────────────────────────
/** "← 목록" 복귀 offset — position null이면 0, 아니면 floor((position−1)/limit)×limit(L1271). */
export const backToListOffset = (position: number | null, limit = 50): number =>
  position == null ? 0 : Math.floor((position - 1) / limit) * limit;

/** CSV 파일명 — dose-review-logs-<시작YYYYMMDD>_<종료YYYYMMDD>-<YYYYMMDDHHmm>.csv (KST, L1300). */
export const csvFilename = (start: string, end: string, now: Date = new Date()): string => {
  const ymd = (d: string) => d.replace(/-/g, "");
  return `dose-review-logs-${ymd(start)}_${ymd(end)}-${kstStamp(now)}.csv`;
};

/** 목록 약품명 — 앞 2개 + "외 N", null 이름은 "이름 없음". full은 title 툴팁용 전체. */
export const productsSummary = (
  products: Array<{ name: string | null }> | null | undefined
): { text: string; full: string } => {
  const names = (products ?? []).map((p) => (p.name && p.name.trim() ? p.name : PRODUCT_NAME_FALLBACK));
  if (names.length === 0) return { text: "", full: "" };
  const full = names.join(", ");
  const head = names.slice(0, 2).join(", ");
  const rest = names.length - 2;
  return { text: rest > 0 ? `${head} 외 ${rest}` : head, full };
};

/**
 * 카드용 request(D-12) — received가 있으면 그것, 없으면 resolved로 합성(received=null은 방어 분기).
 * received.patient·칸 값의 null을 undefined로 정규화(§1.1.7 #14 — ResultView는 request.patient를
 * 직접 읽지 않지만 타입·일관을 위해 정규화).
 */
export const toCardRequest = (
  request: {
    received: DoseReviewLogReceivedPayload | null;
    resolved: DoseReviewLogResolvedPayload;
  } | null
): DoseReviewRequest => {
  if (!request) {
    return { patient: {}, prescriptions: [] } as unknown as DoseReviewRequest;
  }
  const { received, resolved } = request;
  if (received) {
    const patient = received.patient
      ? {
          ageMonths: received.patient.ageMonths ?? undefined,
          weightKg: received.patient.weightKg ?? undefined,
        }
      : {};
    return { patient, prescriptions: received.prescriptions } as unknown as DoseReviewRequest;
  }
  return {
    patient: {
      ageMonths: resolved.ageMonths ?? undefined,
      weightKg: resolved.weightKg ?? undefined,
    },
    prescriptions: resolved.prescriptions,
  } as unknown as DoseReviewRequest;
};

/** versions API로 본문을 받을 rowId 목록 — 중앙 룰(isPharmacyRule=false)만, 중복 제거(결정 E1·L1293). */
export const rowIdsToFetch = (ruleVersions: DoseReviewLogRuleVersion[] | null | undefined): number[] => {
  const seen = new Set<number>();
  const out: number[] = [];
  for (const rv of ruleVersions ?? []) {
    if (rv.isPharmacyRule) continue;
    if (seen.has(rv.rowId)) continue;
    seen.add(rv.rowId);
    out.push(rv.rowId);
  }
  return out;
};
